// Deterministic RNG helpers. Everything in the game derives from one seed string.

export function hashString(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return (h ^ (h >>> 16)) >>> 0;
}

// Combine a base seed with coordinates so a screen always regenerates identically.
export function mix(...nums) {
  let h = 2166136261 >>> 0;
  for (const n of nums) {
    h ^= (n | 0) + 0x9e3779b9;
    h = Math.imul(h, 16777619) >>> 0;
  }
  /* FNV-1a WITHOUT A FINALISER DOES NOT AVALANCHE, AND THE WORLD WAS STRIPED.
   *
   * The loop's last step is one xor and one multiply, and a multiply only
   * carries information UPWARD: bit i of the result depends on bits <= i of
   * the input. So a small change in the last argument moved the low bits and
   * left the top ones almost alone -- and every lattice-noise field in this
   * project reads the TOP bits (`mix(...) >>> 8`).
   *
   * Measured on the grass lattice, holding x and walking y, the hash returned
   * 0.4407 0.4446 0.4486 0.4525 0.4564 ... -- a straight ramp, +0.0039 a step.
   * Not a hash in its second axis at all. Standard deviation along y was 0.103
   * against 0.328 along x, where uniform noise is 0.289: the field varied
   * properly east-west and merely SLOPED north-south, so every field built on
   * it came out as vertical stripes. That is the grass corridor an art critic
   * ranked fourth of seventeen defects on the whole-island render ("a
   * dead-straight 4.5-tile corridor"), the 29-luma column the seam probe kept
   * reporting against a control of 2, and -- the reason this is worth the
   * churn of every seed changing -- a good part of what has been reported from
   * play, over and over, as the world looking "lattice-y".
   *
   * A standard xorshift-multiply finaliser fixes it: sd along y 0.103 -> 0.285
   * against 0.282 along x. */
  h ^= h >>> 16; h = Math.imul(h, 2246822507) >>> 0;
  h ^= h >>> 13; h = Math.imul(h, 3266489909) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

export class RNG {
  s: any;

  constructor(seed) {
    this.s = (typeof seed === 'string' ? hashString(seed) : seed >>> 0) || 1;
  }
  next() {
    let t = (this.s += 0x6d2b79f5) >>> 0;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  int(n) { return Math.floor(this.next() * n); }
  range(a, b) { return a + this.next() * (b - a); }
  irange(a, b) { return a + this.int(b - a + 1); }
  pick(arr) { return arr[this.int(arr.length)]; }
  chance(p) { return this.next() < p; }
  shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }
}

export function randomSeed() {
  const words = ['hyrule', 'gale', 'ember', 'moss', 'crag', 'tide', 'ruin', 'glen', 'onyx', 'fern',
    'gale', 'holt', 'mire', 'peak', 'sable', 'vale', 'wisp', 'thorn'];
  const r = new RNG(Date.now() ^ Math.floor(Math.random() * 0xffffffff));
  return r.pick(words) + '-' + r.pick(words) + '-' + String(r.int(1000)).padStart(3, '0');
}
